'use client'

import { useCallback, useEffect, useId, useRef, useState, type ComponentType, type PointerEvent } from 'react'
import { Bot, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { UserText } from '@/components/ui/user-text'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'

/**
 * The assistant's hello, as the main application says it
 * (`components/ai-chat/chat-welcome.tsx`, drawer mode): the robot in its
 * rounded tile on the dotted `ai-dot-field`, a time-of-day greeting, a line
 * under it, and the suggestions as cards — a small icon, a module pill
 * ("Policy" / "Team"), a title and the question. A card sends its question as
 * the first message.
 *
 * Mirrored rather than imported: the admin welcome is English-only and holds
 * its own suggestion matrix, and its source is pinned by a guard
 * (dot-field-pointer-halo.guard.spec.ts). The classes, the dot field and the
 * pointer halo are the same; here every word goes through t(), the cards are
 * shadcn Buttons, and the module pill keeps its letter spacing to Latin script
 * (spacing pulls Arabic letters apart and breaks Devanagari).
 */

export type GreetingKey =
  | 'knowledgePortal.welcome.hello'
  | 'knowledgePortal.welcome.morning'
  | 'knowledgePortal.welcome.afternoon'
  | 'knowledgePortal.welcome.evening'

/**
 * The greeting for an hour of the reader's own day (the main app's bands:
 * 05–11 morning, 12–16 afternoon, 17–04 evening); a neutral hello before the
 * clock is read, so the server's render and the first client render agree.
 */
export function greetingKeyFor(hour: number | null): GreetingKey {
  if (hour === null) return 'knowledgePortal.welcome.hello'
  if (hour >= 5 && hour < 12) return 'knowledgePortal.welcome.morning'
  if (hour >= 12 && hour < 17) return 'knowledgePortal.welcome.afternoon'
  return 'knowledgePortal.welcome.evening'
}

function useGreeting(): string {
  const { t } = useTranslation()
  const [hour, setHour] = useState<number | null>(null)
  useEffect(() => {
    setHour(new Date().getHours())
  }, [])
  switch (greetingKeyFor(hour)) {
    case 'knowledgePortal.welcome.morning':
      return t('knowledgePortal.welcome.morning')
    case 'knowledgePortal.welcome.afternoon':
      return t('knowledgePortal.welcome.afternoon')
    case 'knowledgePortal.welcome.evening':
      return t('knowledgePortal.welcome.evening')
    default:
      return t('knowledgePortal.welcome.hello')
  }
}

export interface WelcomeSuggestion {
  key: string
  module: 'policy' | 'team'
  /** A short title in the reader's language; the question is the title when there is none. */
  title: string | null
  question: string
  /** True when an admin typed the question (shown as typed text, in its own direction). */
  typed: boolean
  icon: ComponentType<{ className?: string }>
  /** The icon's colour, as the main app's cards have one each. */
  accent: string
}

/**
 * The pointer halo over the dots: the position is written straight onto the
 * node as CSS custom properties, rAF-throttled, never through React state
 * (the main app's handler, for the same reason: it fires on every mousemove).
 */
function useDotFieldHalo() {
  const fieldRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<number | null>(null)
  const pointRef = useRef<{ x: number; y: number } | null>(null)

  const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    pointRef.current = { x: event.clientX, y: event.clientY }
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      const el = fieldRef.current
      const point = pointRef.current
      if (!el || !point) return
      const rect = el.getBoundingClientRect()
      el.style.setProperty('--ai-x', `${point.x - rect.left}px`)
      el.style.setProperty('--ai-y', `${point.y - rect.top}px`)
    })
  }, [])

  const onPointerLeave = useCallback(() => {
    fieldRef.current?.style.setProperty('--ai-y', '-9999px')
  }, [])

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    },
    [],
  )
  return { fieldRef, onPointerMove, onPointerLeave }
}

export function AssistantWelcome({
  name,
  subtitle,
  subtitleTyped,
  policy,
  team,
  onAsk,
  disabled,
}: {
  /**
   * The assistant's name (the admin's, or the default in the reader's language), shown above the
   * greeting: the drawer's header shows the conversation, as the main app's does, so this is the one
   * place a person sees what the admin called the assistant.
   */
  name: string
  /** The line under the greeting: the admin's greeting, or the default sentence. */
  subtitle: string
  /** True when an admin typed the subtitle. */
  subtitleTyped: boolean
  policy: WelcomeSuggestion[]
  team: WelcomeSuggestion[]
  onAsk: (question: string) => void
  /** No question can be sent now (the daily limit, a refusal asking again cannot fix): no cards. */
  disabled: boolean
}) {
  const { t } = useTranslation()
  const greeting = useGreeting()
  const { fieldRef, onPointerMove, onPointerLeave } = useDotFieldHalo()

  return (
    <div
      ref={fieldRef}
      data-slot="assistant-welcome"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      className="ai-dot-field flex min-h-0 flex-1 flex-col"
    >
      <div className="@container relative z-[1] flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-5 pt-6 pb-4">
        <div data-testid="assistant-robot" className="mb-3 flex size-11 items-center justify-center rounded-xl bg-primary/10">
          <Bot className="size-5 text-primary" aria-hidden="true" />
        </div>
        <p data-testid="assistant-name" className="mb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
          <UserText>{name}</UserText>
        </p>
        <h2 className="mb-0.5 text-base font-semibold">{greeting}</h2>
        <p className="mb-4 max-w-sm text-center text-xs text-muted-foreground">
          {subtitleTyped ? <UserText className="whitespace-pre-wrap">{subtitle}</UserText> : subtitle}
        </p>

        {!disabled && (policy.length > 0 || team.length > 0) ? (
          <div className="flex w-full max-w-[min(100%,42rem)] flex-col gap-y-3 sm:max-w-[min(85%,42rem)]">
            {team.length > 0 ? (
              <ul aria-label={t('knowledgePortal.team.suggested')} className="grid grid-cols-1 gap-x-1.5 gap-y-3 @5xl:grid-cols-2">
                {team.map((s) => (
                  <li key={s.key}>
                    <SuggestionCard suggestion={s} onAsk={onAsk} />
                  </li>
                ))}
              </ul>
            ) : null}
            {policy.length > 0 ? (
              <ul aria-label={t('knowledgePortal.panel.suggested')} className="grid grid-cols-1 gap-x-1.5 gap-y-3 @5xl:grid-cols-2">
                {policy.map((s) => (
                  <li key={s.key}>
                    <SuggestionCard suggestion={s} onAsk={onAsk} />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {!disabled ? <p className="mt-3 text-center text-[11px] text-muted-foreground/70">{t('knowledgePortal.welcome.typeYourOwn')}</p> : null}
      </div>
    </div>
  )
}

/** One suggestion: the main app's card, as an outline shadcn Button. */
function SuggestionCard({ suggestion: s, onAsk }: { suggestion: WelcomeSuggestion; onAsk: (question: string) => void }) {
  const { t, locale } = useTranslation()
  // The card is named by its question; the pill and the title describe it.
  const questionId = useId()
  const metaId = useId()
  const pill = s.module === 'team' ? t('knowledgePortal.welcome.pillTeam') : t('knowledgePortal.welcome.pillPolicy')
  // Letter spacing and capitals are a Latin-script treatment.
  const latin = locale === 'en' || locale === 'fr'
  const question = s.typed ? <UserText>{s.question}</UserText> : s.question
  return (
    <Button
      type="button"
      variant="outline"
      data-suggestion={s.module}
      aria-labelledby={questionId}
      aria-describedby={metaId}
      onClick={() => onAsk(s.question)}
      className="group h-auto w-full items-start justify-start gap-2.5 whitespace-normal rounded-lg border-border/60 bg-background/70 py-2 ps-2.5 pe-3 text-start font-normal shadow-none backdrop-blur-sm transition-all hover:border-border hover:bg-background hover:shadow-sm dark:bg-background/70 dark:hover:bg-background"
    >
      <span className={cn('mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-muted/50', s.accent)}>
        <s.icon className="size-3.5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span id={metaId} className="mb-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5">
          <span className={cn('text-[10px] font-semibold text-muted-foreground/80', latin && 'uppercase tracking-wide')}>{pill}</span>
          {s.title ? <span className="text-[13px] leading-tight font-medium text-foreground">{s.title}</span> : null}
        </span>
        <span id={questionId} className={cn('block', s.title ? 'line-clamp-2 text-[11px] leading-snug text-muted-foreground' : 'text-[13px] leading-snug font-medium text-foreground')}>
          {question}
        </span>
      </span>
      <span aria-hidden="true" className="mt-1 shrink-0">
        <ChevronRight className="rtl-flip size-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
      </span>
    </Button>
  )
}
